import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Container, Form, Spinner } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useLogoUrl } from '../lib/configSistema';
import CampoSenha from '../components/CampoSenha';
import { destinoPermitido } from '../lib/menu';
import './Login.css';

// Visual réplica do login antigo (`painel.eimovel.com.br (laravel antigo)`,
// resources/views/login/login.blade.php + public/vendor/bootstrap/login.css):
// mesmo fundo em gradiente azul (`#004d8e`/`#3aa5ff`/`#097fe2`, cores exatas
// do CSS original), mesma logo, mesma animação de nuvens flutuantes (pedido
// explícito do Jason 2026-09-18 — tinha sido removida por engano na v1
// achando que era só um efeito datado, mas faz parte do visual que ele quer
// preservado), mesmo botão flutuante de WhatsApp de suporte. Modernizado só
// o cartão de login em si (sombra/bordas do Bootstrap 5 em vez do form cru).
const WHATSAPP_SUPORTE = 'https://api.whatsapp.com/send?phone=5519997755909&text=Ol%C3%A1,%20estou%20no%20Painel%20EIMOVEL%20e%20preciso%20de%20ajuda.';

// Mesma sequência de classes Foreground/Background do markup original
// (login.blade.php) — a posição de cada nuvem no CSS (nth-child) depende
// dessa ordem exata, não é aleatória.
const NUVENS = ['frente', 'fundo', 'frente', 'fundo', 'frente', 'fundo', 'fundo', 'frente', 'fundo', 'fundo'];

export default function Login() {
  const { entrar } = useAuth();
  const toast = useToast();
  const logoUrl = useLogoUrl();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [enviando, setEnviando] = useState(false);

  // Link de auto-login do bot do Telegram expirado (token de 5 min, ver controllers/auth.js
  // `autoLogin`) — avisa SÓ nesse momento, sem poluir a tela de login o resto do tempo (pedido do
  // Jason, 21/10/2026: "ela entrando com user e senha de novo, o painel direciona pra onde ela
  // ia" — por isso o `destino` da URL alimenta o mesmo `navigate` de baixo).
  const destinoExpirado = searchParams.get('destino');
  useEffect(() => {
    if (searchParams.get('expirado') === '1') {
      toast.erro('Esse link expirou (vale por 15 minutos e só abre uma vez) — entre normalmente que a gente já te leva pro mesmo lugar.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function aoEnviar(e) {
    e.preventDefault();
    setEnviando(true);
    try {
      const logado = await entrar(email, senha);
      // Volta pra URL que o usuário tentou abrir antes de cair aqui — de RotaProtegida (`state.de`)
      // OU de um link de auto-login expirado (`?destino=` na URL, só usa se começar com "/").
      const candidato = location.state?.de || (destinoExpirado?.startsWith('/') ? destinoExpirado : null) || '/';
      // só volta para lá se ESTE usuário pode abrir a página (ex.: a página lembrada era /empresas e quem entrou é admin da imobiliária)
      navigate(destinoPermitido(candidato, logado) ? candidato : '/', { replace: true });
    } catch (err) {
      toast.erro(err.message || 'Não foi possível entrar');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Container
      fluid
      className="d-flex align-items-center justify-content-center"
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(circle, #3aa5ff 0%, #097fe2 70%, #004d8e 100%)',
      }}
    >
      <div className="nuvens">
        {NUVENS.map((tipo, i) => (
          <div key={i} className={`nuvem ${tipo}`} />
        ))}
      </div>

      <Card style={{ width: '100%', maxWidth: 380, zIndex: 1 }} className="shadow-lg border-0">
        <Card.Body className="p-4">
          <div className="text-center mb-4">
            <img src={logoUrl} alt="EIMOVEL" style={{ maxWidth: 220 }} />
          </div>

          <Form onSubmit={aoEnviar}>
            <Form.Group className="mb-3" controlId="email">
              <Form.Label>E-mail</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </Form.Group>
            <Form.Group className="mb-4" controlId="senha">
              <Form.Label>Senha</Form.Label>
              <CampoSenha
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                required
              />
            </Form.Group>
            <Button type="submit" variant="primary" className="w-100 text-uppercase fw-bold" disabled={enviando}>
              {enviando ? <Spinner animation="border" size="sm" /> : 'Entrar'}
            </Button>
            <div className="text-center mt-3">
              <Link to="/esqueci-senha">Esqueci minha senha</Link>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <a
        href={WHATSAPP_SUPORTE}
        target="_blank"
        rel="noreferrer"
        className="d-flex align-items-center justify-content-center"
        style={{
          position: 'fixed',
          width: 60,
          height: 60,
          bottom: 20,
          right: 20,
          backgroundColor: '#25d366',
          color: '#fff',
          borderRadius: 50,
          fontSize: 30,
          boxShadow: '1px 1px 2px #888',
        }}
      >
        <i className="bi bi-whatsapp" />
      </a>
    </Container>
  );
}
